import { useEffect, useState } from "react";
import client, { assetUrl } from "@/lib/glossyApi";
import { Download, Copy, Link2, RefreshCw, Share2 } from "lucide-react";
import { toast } from "sonner";

async function downloadFile(path, filename) {
  try {
    const res = await fetch(assetUrl(path));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed. Try again.");
  }
}

function copyText(text, msg) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(msg),
    () => toast.error("Could not copy.")
  );
}

// Ready-to-post social cards + captions for every published piece about the member.
// adminMode adds a "Copy launch note" for the editor's "your feature is live" send-out.
export default function ShareKit({ endpoint = "/members/me/share-kit", adminMode = false, ownerName = "", className = "mt-14" }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setBusy(true);
    try {
      const { data } = await client.get(`${endpoint}${refresh ? "?refresh=true" : ""}`);
      setItems(data.items || []);
      if (refresh) toast.success("Share cards rebuilt with your latest imagery.");
    } catch {
      setItems([]);
    }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const launchNote = (it, link) => {
    const first = (ownerName || "").split(" ")[0] || "there";
    return `Hi ${first} — great news: your ${it.label} is live in S/ME!\n\nRead it here: ${link}\n\nYour ready-to-post share kit is waiting in your member portal (Portal → Share your features) — download the cards, copy the caption and post away. The more you share it, the more readers find you.\n\n— The Editors, S/ME`;
  };

  return (
    <section className={className} data-testid="share-kit-section">
      <div className="flex items-end justify-between border-b border-ink/20 pb-4 mb-3">
        <h2 className="font-grotesk text-2xl sm:text-3xl font-medium flex items-center gap-3"><Share2 size={24} strokeWidth={1.5} /> Share your features</h2>
        {items?.length > 0 && (
          <button onClick={() => load(true)} disabled={busy} className="overline flex items-center gap-2 text-ink/60 hover:text-ink transition-colors disabled:opacity-50" data-testid="share-kit-refresh">
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> {busy ? "Rebuilding…" : "Rebuild cards"}
          </button>
        )}
      </div>
      <p className="font-sans text-sm text-ink/60 mb-6 max-w-2xl">
        {adminMode
          ? "Every published piece comes with ready-made social cards and a caption. Send the launch note to the member so they post their feature — their audience becomes the magazine's audience."
          : "Every published piece about you comes with ready-made social cards and a caption — download, copy, post. Your audience sees your feature; the magazine finds new readers. Everyone wins."}
      </p>

      {items === null && <p className="font-mono text-xs text-ink/40 py-6">Preparing your share kit…</p>}
      {items?.length === 0 && (
        <p className="font-sans text-ink/50 border border-dashed border-ink/25 p-8 text-center" data-testid="share-kit-empty">
          {adminMode ? "The share kit appears here as soon as a piece is published for this member." : "Your share kit appears here the moment your first piece is published."}
        </p>
      )}

      <div className="space-y-6">
        {items?.map((it) => {
          const link = `${window.location.origin}/read/${it.slug}`;
          return (
            <div key={it.article_id} className="border border-ink/15 p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="share-kit-item">
              {/* previews */}
              <div className="lg:col-span-4 flex items-start gap-4">
                {it.post_card ? (
                  <img src={assetUrl(it.post_card)} alt={`${it.title} — post card`} className="w-1/2 aspect-square object-cover border border-ink/10" data-testid="share-kit-post-preview" />
                ) : (
                  <div className="w-1/2 aspect-square bg-ink/5 flex items-center justify-center font-mono text-[10px] text-ink/40 uppercase">No card yet</div>
                )}
                {it.story_card ? (
                  <img src={assetUrl(it.story_card)} alt={`${it.title} — story card`} className="w-[28%] aspect-[9/16] object-cover border border-ink/10" data-testid="share-kit-story-preview" />
                ) : (
                  <div className="w-[28%] aspect-[9/16] bg-ink/5 flex items-center justify-center font-mono text-[10px] text-ink/40 uppercase">—</div>
                )}
              </div>
              {/* copy + actions */}
              <div className="lg:col-span-8">
                <p className="overline text-gloss-gold">{it.label}</p>
                <h3 className="font-serif text-2xl tracking-tight mt-1 leading-tight">{it.title}</h3>
                <p className="font-sans text-sm text-ink/70 mt-3 leading-relaxed border-l-2 border-ink/15 pl-4 max-w-xl">{it.caption} <span className="text-ink/40 break-all">{link}</span></p>
                <div className="flex flex-wrap gap-3 mt-5">
                  {it.post_card && (
                    <button onClick={() => downloadFile(it.post_card, `sme-${it.slug}-post.jpg`)} className="overline flex items-center gap-2 border border-ink/40 px-4 py-2.5 hover:bg-ink hover:text-cream transition-colors" data-testid="share-kit-download-post">
                      <Download size={13} /> Post (square)
                    </button>
                  )}
                  {it.story_card && (
                    <button onClick={() => downloadFile(it.story_card, `sme-${it.slug}-story.jpg`)} className="overline flex items-center gap-2 border border-ink/40 px-4 py-2.5 hover:bg-ink hover:text-cream transition-colors" data-testid="share-kit-download-story">
                      <Download size={13} /> Story (vertical)
                    </button>
                  )}
                  <button onClick={() => copyText(`${it.caption}\n\n${link}`, "Caption copied — paste it into your post.")} className="overline flex items-center gap-2 border border-ink/40 px-4 py-2.5 hover:bg-ink hover:text-cream transition-colors" data-testid="share-kit-copy-caption">
                    <Copy size={13} /> Copy caption
                  </button>
                  <button onClick={() => copyText(link, "Link copied.")} className="overline flex items-center gap-2 border border-ink/40 px-4 py-2.5 hover:bg-ink hover:text-cream transition-colors" data-testid="share-kit-copy-link">
                    <Link2 size={13} /> Copy link
                  </button>
                  {adminMode && (
                    <button onClick={() => copyText(launchNote(it, link), "Launch note copied — send it to the member.")} className="overline flex items-center gap-2 bg-ink text-cream px-4 py-2.5 hover:bg-gloss-gold hover:text-ink transition-colors" data-testid="share-kit-copy-note">
                      <Copy size={13} /> Copy "it's live" note
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
